import { Head, usePage } from "@inertiajs/react"
import { CheckCircle, Clock, Lock, Mail, ShieldCheck } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { isApproved, isVerified } from "@/lib/user-utils"
import { SharedData } from "@/types"

export default function Waitlisted() {
  const { auth } = usePage<SharedData>().props
  const { user } = auth

  const userVerified = isVerified(user)
  const userApproved = isApproved(user)

  return (
    <AppLayout breadcrumbs={[{ title: "Waitlist", href: "/waitlisted" }]}>
      <Head title="You're on the Waitlist - ThaiCopilot">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
        <div className="w-full max-w-2xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl sm:p-12 dark:border-slate-800 dark:bg-slate-900">
            {/* Header Section */}
            <div className="mb-10 text-center">
              <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-950">
                <Clock className="h-10 w-10 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl dark:text-white">
                You're on the Waitlist
              </h1>
              <p className="mx-auto max-w-lg text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                ThaiCopilot has limited early-access spots. You’ve been added to
                the waitlist and will receive an email shortly.
              </p>

              <div className="mt-6 rounded-lg bg-indigo-50 p-4 text-sm text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-200">
                <p className="font-medium">
                  Want faster approval? Reply to the welcome email — the founder
                  sees those personally.
                </p>
              </div>
            </div>

            {/* Progress Section */}
            <div className="rounded-xl bg-slate-50 p-8 dark:bg-slate-950/50">
              <h2 className="mb-6 text-lg font-semibold text-slate-900 dark:text-white">
                Your Status
              </h2>

              <div className="relative space-y-8">
                {/* Connecting Line */}
                <div
                  className="absolute top-3 left-3.5 h-full w-0.5 bg-slate-200 dark:bg-slate-800"
                  aria-hidden="true"
                />

                {/* Step 1: Verify Email */}
                <div className="relative flex gap-4">
                  <div
                    className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 ${userVerified ? "border-green-500 bg-green-500 text-white" : "border-amber-500 bg-white text-amber-500 dark:bg-slate-900"}`}
                  >
                    {userVerified ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                  </div>
                  <div className="pt-0.5">
                    <h3
                      className={`font-medium ${userVerified ? "text-green-600 dark:text-green-400" : "text-slate-900 dark:text-white"}`}
                    >
                      {userVerified ? "Email verified" : "Verify your email"}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {userVerified
                        ? "Signed in with Google"
                        : "Check your inbox for a verification link"}
                    </p>
                  </div>
                </div>

                {/* Step 2: Wait for Approval */}
                <div className="relative flex gap-4">
                  <div
                    className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 ${userApproved ? "border-green-500 bg-green-500 text-white" : userVerified ? "border-indigo-500 bg-white text-indigo-500 dark:bg-slate-900" : "border-slate-300 bg-white text-slate-300 dark:border-slate-700 dark:bg-slate-900"}`}
                  >
                    {userApproved ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <ShieldCheck className="h-4 w-4" />
                    )}
                  </div>
                  <div className="pt-0.5">
                    <h3
                      className={`font-medium ${userApproved ? "text-slate-900 dark:text-white" : "text-slate-900 dark:text-white"}`}
                    >
                      Wait for approval
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Usually takes 1–2 working days
                    </p>
                  </div>
                </div>

                {/* Step 3: Full Access */}
                <div className="relative flex gap-4">
                  <div
                    className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 ${userApproved ? "border-green-500 bg-green-500 text-white" : "border-slate-300 bg-white text-slate-300 dark:border-slate-700 dark:bg-slate-900"}`}
                  >
                    {userApproved ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                  </div>
                  <div className="pt-0.5">
                    <h3
                      className={`font-medium ${userApproved ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
                    >
                      Full access
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Unlock all ThaiCopilot features
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
